import { ApiResponse } from "@/utils/ApiResponse";
import type { QueryKey } from "@tanstack/vue-query";
import { useQueryClient, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import { CategoriasProductoInterface } from "../../interfaces/categoriaInterfaces/CategoriasProducto.interface";
import { ToggleVisibilityCategoriaProductoAction } from "../../actions/categoriaActions";

const toast = useToast();

type ToggleCtx = {
  previousEntries: [QueryKey, unknown][];
};

export function useCategoriaProductoToggleVisib() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (categoriaId: string) =>
      ToggleVisibilityCategoriaProductoAction(categoriaId),

    onMutate: async (categoriaId: string): Promise<ToggleCtx> => {
      await queryClient.cancelQueries({ queryKey: ["CategoriaProductosList"] });
      const previousEntries = queryClient.getQueriesData({
        queryKey: ["CategoriaProductosList"],
      });

      queryClient.setQueriesData<ApiResponse<CategoriasProductoInterface>>(
        { queryKey: ["CategoriaProductosList"] },
        (old) => {
          if (!old?.data?.data?.categorias) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                categorias: old.data.data.categorias.map((categoria) =>
                  categoria.id === categoriaId
                    ? {
                        ...categoria,
                        isVisible: categoria.isVisible === 1 ? 0 : 1,
                      }
                    : categoria
                ),
              },
            },
          };
        }
      );

      return { previousEntries };
    },

    onError: (_error, _categoriaId, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error("No se pudo actualizar la visibilidad de la categoría.");
    },

    onSuccess: () => {
      toast.success("Visibilidad de categoría actualizada.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["CategoriaProductosList"],
      });
    },
  });

  return {
    ...mutation,
  };
}
